import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  ContactReferenceModel,
  MerchantAccount,
  PaymentMethodModel,
  PriceCancellationTerms,
  PricePageRecurringInputModel,
  PurchasePaymentIntentModel,
  SubcriptionAdditionalFeesModel,
  SubscriptionModel,
} from "../models";
import {
  AuthService,
  ContactService,
  MerchantAccountService,
  PaymentMethodService,
  SubscriptionService,
} from "../services";
import {
  CollapsiblePanel,
  CustomButton,
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  Loader,
  ModalComponent,
  nameof,
  PriceDescriptionBlock,
  SapphireBlueButton,
  SingleSelect,
  TextInputWithDropdown,
  TimeAMPM,
  ToastService,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { getBillingOptions } from "./RecurringSection";
import {
  ApplicationStatus,
  ItemBillingEnds,
  RecurringBillingInterval,
  ItemBillingEndsAfterInterval,
} from "../enums";
import {
  DescriptionCell,
  getOptions,
  InvoicePopupWrapper,
  MainContent,
  ProductDetailWrapper,
  SetupDetails,
} from "../styles";
import PriceIntervalSection from "./PriceIntervalSection";
import RecurringFeeSection from "./RecurringFeeSection";
import { CancellationRow, NoticeRow } from "./AdvancedSection";
import ContactDropDown from "./ContactDropDown";
import RenderContactInfo from "./RenderContactInfo";
import { SubscriptionUtil } from "../utils";
import { AccountSettingsStore } from "../store";
import MerchantAccountsDropDown from "./MerchantAccountsDropDown";
import PaymentMethodDropdown from "./PaymentMethodDropdown";
import BillingMethods from "./BillingMethods";
import NewContactPopup from "./NewContactPopup";

type SingleValue =
  | string
  | boolean
  | Date
  | ContactReferenceModel
  | MerchantAccount
  | PaymentMethodModel
  | number
  | PurchasePaymentIntentModel;
type ArrayValue =
  | MerchantAccount[]
  | ContactReferenceModel[]
  | PaymentMethodModel[];
type FieldValue = SingleValue | ArrayValue;

type IProps = {
  onClose: (refresh: boolean, contactId?: string) => void;
  selectedContact?: ContactReferenceModel | null;
  isEdit?: boolean;
  subscriptionId?: string;
  subscriptionData: SubscriptionModel;
};

const NewSubscriptionPopUp: React.FC<IProps> = ({
  onClose,
  selectedContact,
  isEdit,
  subscriptionId,
  subscriptionData,
}) => {
  const [isAPIRequestPending, setAPIRequestPending] = React.useState(false);
  const [data, setData] = useState<SubscriptionModel>(
    new SubscriptionModel({
      ...subscriptionData,
      isContactRequired:
        !selectedContact?.id && AuthService.isMindMeContactRoute ? false : true,
    })
  );
  const [showContactPopup, setShowContactPopup] = React.useState(false);
  const [showLoader, setShowLoader] = React.useState(true);
  const [hasMoreContacts, setHasMoreContacts] = React.useState(false);

  useEffect(() => {
    loadSubscriptionData();
  }, [isEdit]);

  const loadSubscriptionData = async () => {
    try {
      setShowLoader(true);

      if (isEdit) {
        const subscriptionResponse = await SubscriptionService.getSubscription(
          AuthService.authData.accountId,
          subscriptionId || "",
          ""
        ).toPromise();
        if (subscriptionResponse?.result) {
          const details = SubscriptionModel.deserializeDetailAPIModel(
            subscriptionResponse.result.detail
          );
          setData(details);
        } else {
          ToastService.showError("Error in getting subscription");
        }
      }
      await Promise.all([
        fetchMerchantAccounts(),
        fetchPaymentMethods(),
        // fetchRecentCustomerActivities(),
        loadContacts(selectedContact?.id, selectedContact?.mindmeContactId),
      ]);
    } catch (error) {
      console.error("Error loading subscription data:", error);
      ToastService.showError("Error loading data");
    } finally {
      setShowLoader(false);
    }
  };

  const fetchMerchantAccounts = async () => {
    const response = await MerchantAccountService.getMerchantAccounts(
      AuthService.authData.accountId,
      1,
      10000,
      ""
    ).toPromise();

    if (response?.result) {
      const activeMerchantAccounts = (
        response.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) ||
        []
      ).filter((f) => f.applicationStatus === ApplicationStatus.Active);

      const defaultMerchantAccount =
        activeMerchantAccounts?.length > 0 ? activeMerchantAccounts[0] : null;

      setData(
        (prevData) =>
          new SubscriptionModel({
            ...prevData,
            selectedMerchantAccount:
              prevData.selectedMerchantAccount.name === "" &&
              defaultMerchantAccount
                ? defaultMerchantAccount
                : prevData.selectedMerchantAccount,
            merchantAccounts: activeMerchantAccounts,
            selectedContact: selectedContact
              ? selectedContact
              : new ContactReferenceModel(),
          })
      );
    }
  };

  const fetchPaymentMethods = async () => {
    const response = await PaymentMethodService.getPaymentMethods().toPromise();

    if (response?.result) {
      const paymentMethods = response.result.map((paymentMethod) =>
        PaymentMethodModel.deserializeAPIModel(paymentMethod)
      );

      const selectedPaymentMethod =
        PaymentMethodModel.getDefaultSelecedPaymentMethod(
          paymentMethods,
          data.selectedContact?.id || ""
        );

      setData(
        (prevData) =>
          new SubscriptionModel({
            ...prevData,
            paymentMethods: paymentMethods,
            selectedPaymentMethod: isEdit
              ? prevData.selectedPaymentMethod
              : selectedPaymentMethod,
          })
      );
    }
  };

  const onCloseContact = (refresh: boolean) => {
    if (refresh) {
      loadSubscriptionData();
    }
    setShowContactPopup(false);
  };

  function loadContacts(
    paymentContactId?: string,
    mindmeContactId?: string,
    searchValue?: string,
    pageSize: number = 50
  ) {
    ContactService.getMindMeContacts( 
      AuthService.authData.accountId,
      paymentContactId,
      mindmeContactId,
      searchValue,
      pageSize
    ).subscribe({
      next: (response) => {
        const contacts =
          response.result?.data?.map((contact) => {
            return ContactReferenceModel.deserializeFromMindMeContacts(contact);
          }) || [];
        setHasMoreContacts(response.result?.hasMoreData || false);
        const contactDetail = contacts.find(
          (contact) =>
            (paymentContactId && contact.id === paymentContactId) ||
            (mindmeContactId && contact.mindmeContactId === mindmeContactId)
        );
        setData(
          (prevData) =>
            new SubscriptionModel({
              ...prevData,
              contacts,
              selectedContact: contactDetail,
            })
        );
      },
    });
  }

  const row = (
    label: string,
    rightSide: React.ReactNode,
    spacing: number = 20,
    className?: string
  ) => {
    return (
      <FlexContainer spacing={30} className={className}>
        <CustomLabel>{label}</CustomLabel>
        <div>
          <FlexContainer alignItems="center" spacing={spacing}>
            {rightSide}
          </FlexContainer>
        </div>
      </FlexContainer>
    );
  };

  const singleSelect = (
    field:
      | keyof SubscriptionModel
      | keyof SubcriptionAdditionalFeesModel
      | keyof PriceCancellationTerms,
    selectedKey: string,
    className: string,
    options: IOption[],
    error: boolean,
    errorMessage: string
  ): React.ReactNode => {
    return (
      <SingleSelect
        selectedKey={selectedKey}
        hasError={error}
        searchable={false}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options || []]}
        errorMessage={errorMessage}
        onChange={(option: any) => handleInputChange(field, option.value)}
        className={`${className} dpdn-no-separator`}
      />
    );
  };

  const textInput = (
    field:
      | keyof SubscriptionModel
      | keyof SubcriptionAdditionalFeesModel
      | keyof PriceCancellationTerms,
    type: string,
    value: string | Date,
    className: string,
    error: boolean,
    placeholder: string,
    maxLength?: number
  ) => {
    return (
      <Input
        type={type as "text" | "textarea" | "number" | "date"}
        value={value}
        error={error}
        errorMessage={""}
        className={`styled-input merchant-inputs ${className}`}
        onChange={(e, isBlur) =>
          handleInputChange(field, e.target.value, isBlur)
        }
        placeholder={placeholder}
        onDateChange={(date) => handleInputChange(field, new Date(date))}
        maxLength={maxLength}
      />
    );
  };

  const textInputRow = (
    label: string,
    field:
      | keyof SubscriptionModel
      | keyof SubcriptionAdditionalFeesModel
      | keyof PriceCancellationTerms,
    type: "text" | "textarea" | "number" | "date",
    value: any,
    hasError: boolean,
    placeholder = "Enter value",
    spacing = 20
  ) =>
    row(
      label,
      textInput(field, type, value, "number-selector", hasError, placeholder),
      spacing
    );

  const formatAmount = (value: string): string => {
    if (parseFloat(value) < 0 || isNaN(parseFloat(value))) {
      return "0.00";
    }
    return AccountSettingsStore.formatAmountWithoutCommas(
      parseFloat(value.toString().replace(/,/g, "") || "0.00"),
      true
    );
  };

  const shouldResetAdditionalFees = (
    billingInterval: keyof typeof RecurringBillingInterval,
    billingIntervalCount: number
  ) =>
    !data.isBillEveryGreaterThan3Months(billingInterval, billingIntervalCount);

  const handleInputChange = (
    field:
      | keyof SubscriptionModel
      | keyof SubcriptionAdditionalFeesModel
      | keyof PriceCancellationTerms,
    value: FieldValue,
    isBlur?: boolean
  ) => {
    setData((prevData) => {
      const clonedData = new SubscriptionModel({ ...prevData });
      if (field in prevData.additionalFees) {
        clonedData.additionalFees = new SubcriptionAdditionalFeesModel({
          ...prevData.additionalFees,
          [field]: value,
        });
        return new SubscriptionModel(clonedData);
      }
      if (field in prevData.cancellationTerms) {
        clonedData.cancellationTerms = new PriceCancellationTerms({
          ...prevData.cancellationTerms,
          [field]: value,
        });
        return new SubscriptionModel(clonedData);
      }

      if (
        [
          nameof<SubscriptionModel>("setupFee"),
          nameof<SubscriptionModel>("recurringPrice"),
          nameof<SubscriptionModel>("recurringCost"),
        ].includes(field as keyof SubscriptionModel)
      ) {
        if (isBlur) {
          value = formatAmount(value as string);
        }
        value = value as string;
      }

      if (field === nameof<SubscriptionModel>("billingInterval")) {
        const interval = value as keyof typeof RecurringBillingInterval;
        clonedData["billingInterval"] = interval;

        if (shouldResetAdditionalFees(interval, data.billingIntervalCount)) {
          clonedData.additionalFees = new SubcriptionAdditionalFeesModel({
            ...prevData.additionalFees,
            increasePriceInterval: "None",
            recurringFeeInterval: "None",
          });
        }

        return new SubscriptionModel(clonedData);
      }

      if (field === nameof<SubscriptionModel>("billingIntervalCount")) {
        const count = value as number;
        clonedData["billingIntervalCount"] = count;

        if (shouldResetAdditionalFees(data.billingInterval, count)) {
          clonedData.additionalFees = new SubcriptionAdditionalFeesModel({
            ...prevData.additionalFees,
            increasePriceInterval: "None",
            recurringFeeInterval: "None",
          });
        }

        return new SubscriptionModel(clonedData);
      }

      clonedData[field] = value;
      return new SubscriptionModel(clonedData);
    });
  };

  const billingEndsBlock = () => {
    return (
      <PriceDescriptionBlock>
        <div>
          <FlexContainer className="price-flex">
            <CustomLabel color="#000">
              $
              {AccountSettingsStore.formatAmount(
                parseFloat(data.setupFee) || 0,
                true
              )}
            </CustomLabel>
            <div className="width-inherit">
              <FlexContainer className="price-sub-flex">
                <CustomLabel>Due</CustomLabel>
                <CustomLabel fontWeight={"400"}>
                  {data.setupFee === "0" ? "No billing today" : "Billed today"}
                </CustomLabel>
              </FlexContainer>
            </div>
          </FlexContainer>
        </div>
        <div>
          <FlexContainer className="price-flex">
            <CustomLabel color="#000">
              $
              {AccountSettingsStore.formatAmount(
                parseFloat(data.recurringPrice) || 0,
                true
              )}
            </CustomLabel>
            <div className="width-inherit">
              <FlexContainer fxLayout="column" spacing={5}>
                <div className="width-inherit">
                  <FlexContainer className="price-sub-flex">
                    <CustomLabel>Starts</CustomLabel>
                    <CustomLabel fontWeight={"400"}>
                      {UtilityMethods.formatDateInString(data.paymentDueDate)}
                    </CustomLabel>
                  </FlexContainer>
                </div>
                <div className="width-inherit">
                  <FlexContainer className="price-sub-flex">
                    <CustomLabel>Recurs</CustomLabel>
                    <CustomLabel fontWeight={"400"}>
                      Every {data.billingIntervalCount} {data.billingInterval}
                      {data.billingIntervalCount > 1 ? "s" : ""}
                    </CustomLabel>
                  </FlexContainer>
                </div>
                <div className="width-inherit">
                  <FlexContainer className="price-sub-flex">
                    <CustomLabel>Ends</CustomLabel>
                    <CustomLabel fontWeight={"400"}>
                      {data.billingEnds === ItemBillingEnds.After
                        ? `After ${data.billingEndIntervalCount.toString()} ${data.billingEndsAfterInterval}${data.billingEndIntervalCount > 1 && data.billingEndsAfterInterval !== ItemBillingEndsAfterInterval.Payments ? "s" : ""}`
                        : "On Cancellation"}
                    </CustomLabel>
                  </FlexContainer>
                </div>
              </FlexContainer>
            </div>
          </FlexContainer>
        </div>
      </PriceDescriptionBlock>
    );
  };

  const billingEndsContent = () => {
    const billingEndsSelect = singleSelect(
      "billingEnds",
      data.billingEnds,
      "number-selector subs-billing-end-dpdn",
      getBillingOptions("billingEnds"),
      data.hasError("billingEnds"),
      ""
    );

    if (data.billingEnds === ItemBillingEnds.After) {
      return (
        <FlexContainer fxLayout="column">
          <FlexContainer spacing={15} fxLayout="row" className="price-field">
            {billingEndsSelect}
            {textInput(
              "billingEndIntervalCount",
              "number",
              data.billingEndIntervalCount.toString(),
              "number-selector subs-billing-count",
              data.hasError("billingEndIntervalCount"),
              "",
              3
            )}
            {singleSelect(
              "billingEndsAfterInterval",
              data.billingEndsAfterInterval,
              "number-selector subs-billing-dpdn",
              getBillingOptions(
                "billingEndsAfterInterval",
                data.billingEndIntervalCount > 1
              ),
              data.hasError("billingEndsAfterInterval"),
              ""
            )}
          </FlexContainer>
          {billingEndsBlock()}
        </FlexContainer>
      );
    }

    return (
      <FlexContainer fxLayout="column">
        {billingEndsSelect}
        {billingEndsBlock()}
      </FlexContainer>
    );
  };

  const disableDropdownForPriceIncrease = isEdit
    ? data.isBillEveryGreaterThan3Months(
        data.billingInterval,
        data.billingIntervalCount
      )
      ? false
      : data.additionalFees.increasePriceInterval !== "None"
        ? false
        : true
    : !data.isBillEveryGreaterThan3Months(
        data.billingInterval,
        data.billingIntervalCount
      );

  const disableDropdownForRecurringFee = isEdit
    ? data.isBillEveryGreaterThan3Months(
        data.billingInterval,
        data.billingIntervalCount
      )
      ? false
      : data.additionalFees.recurringFeeInterval !== "None"
        ? false
        : true
    : !data.isBillEveryGreaterThan3Months(
        data.billingInterval,
        data.billingIntervalCount
      );

  const renderAdvancedSection = () => {
    return (
      <MainContent className="main-content">
        <ProductDetailWrapper className="width-inherit">
          <SetupDetails>
            <CollapsiblePanel
              isOpened={false}
              title={"Advanced"}
              className="advanced-section-styles"
            >
              <div>
                <CustomLabel className="semi-header">
                  {" "}
                  Additional Fees
                </CustomLabel>
              </div>
              <div>
                <PriceIntervalSection
                  increasePriceInterval={
                    data.additionalFees.increasePriceInterval || ""
                  }
                  increasePriceValue={
                    data.additionalFees.increasePriceAmount || ""
                  }
                  increasePriceBy={
                    data.additionalFees.increasePriceBy || "Amount"
                  }
                  onIncreasePriceIntervalChange={(value: string) =>
                    handleInputChange("increasePriceInterval", value)
                  }
                  onIncreasePriceValueChange={(value: string) =>
                    handleInputChange("increasePriceAmount", value)
                  }
                  onIncreasePriceByChange={(value: string) =>
                    handleInputChange("increasePriceBy", value)
                  }
                  getOptions={(fieldName) =>
                    getOptions(fieldName as keyof PricePageRecurringInputModel)
                  }
                  hasError={(fieldName) =>
                    data.additionalFees.hasError(
                      fieldName as keyof SubcriptionAdditionalFeesModel
                    )
                  }
                  className="subs-price-interval"
                  showMerged={true}
                  disablePriceIntervalSelect={disableDropdownForPriceIncrease}
                  errorMessage={
                    data.additionalFees.errorMessage("increasePriceInterval") ||
                    data.additionalFees.errorMessage("increasePriceAmount")
                  }
                />
              </div>
              <RecurringFeeSection
                feeInterval={data.additionalFees.recurringFeeInterval || ""}
                fee={
                  data.additionalFees.recurringFee !== ""
                    ? data.additionalFees.recurringFee
                    : ""
                }
                onFeeIntervalChange={(value: string) =>
                  handleInputChange("recurringFeeInterval", value)
                }
                onFeeChange={(value: string) =>
                  handleInputChange("recurringFee", value)
                }
                fieldname="feeInterval"
                hasError={(fieldName) =>
                  data.additionalFees.hasError(
                    fieldName as keyof SubcriptionAdditionalFeesModel
                  )
                }
                className="subs-recurring-fee"
                recurringSelect="dpdn-no-separator"
                disableRecurringSelect={disableDropdownForRecurringFee}
                errorMessage={
                  data.additionalFees.errorMessage("recurringFee") ||
                  data.additionalFees.errorMessage("recurringFeeInterval")
                }
              />

              <div>
                <CustomLabel className="semi-header setup">
                  {" "}
                  Cancellation Terms
                </CustomLabel>
              </div>
              <NoticeRow
                label="Required Notice"
                value={data.cancellationTerms.daysNotice.toString() || ""}
                hasError={data.cancellationTerms.hasError("daysNotice")}
                onChange={(value: string) =>
                  handleInputChange("daysNotice", value)
                }
              />
              <CancellationRow
                label="Minimum Due"
                value={data.cancellationTerms.minimumDue.toString() || ""}
                hasError={data.cancellationTerms.hasError("minimumDue")}
                onChange={(value: string) =>
                  handleInputChange("minimumDue", value)
                }
              />
              <CancellationRow
                label="Cancellation Fee"
                value={data.cancellationTerms.cancellationFee.toString() || ""}
                hasError={data.cancellationTerms.hasError("cancellationFee")}
                onChange={(value: string) =>
                  handleInputChange("cancellationFee", value)
                }
              />
            </CollapsiblePanel>
          </SetupDetails>
        </ProductDetailWrapper>
      </MainContent>
    );
  };

  function updateTime(
    amPm: keyof typeof TimeAMPM,
    time: string,
    isBlur: boolean
  ) {
    if (time) {
      time = time.replace(/[^0-9:]/g, ""); // Remove non-digits and colons
      if (isBlur) {
        const match = time.match(/(\d{1,2}):(\d{1,2})/);
        if (match) {
          let [_, hours, minutes] = match;
          // Ensure hours and minutes are zero-padded
          hours = hours.padStart(2, "0");
          minutes = minutes.padStart(2, "0");

          // Return the formatted time string
          time = `${hours}:${minutes}`;
        }
      }
    }
    setData(
      (prevData) =>
        new SubscriptionModel({
          ...prevData,
          paymentTime: time,
          paymentMeredium: amPm,
        })
    );
  }

  // const handlePaymentSuccess = (token: string) => {
  //     setData(
  //         (prevData) =>
  //             new SubscriptionModel({
  //                 ...prevData,
  //                 paymentMethodId: token,
  //             }),
  //     );
  // };

  // const handlePaymentError = () => {
  //     setData((prevData) => new SubscriptionModel({ ...prevData, paymentMethodId: '' }));
  // };

  function onNewPaymentMethod(isNewPaymentMethod: boolean, token: string) {
    setData(
      (prevData) =>
        new SubscriptionModel({
          ...prevData,
          newPaymentGatewayMethodId: token,
          isNewPaymentMethod: isNewPaymentMethod,
        })
    );
  }

  function onContactChange(contact: ContactReferenceModel) {
    const defaultPaymentMethod =
      PaymentMethodModel.getDefaultSelecedPaymentMethod(
        data.paymentMethods,
        contact.id
      );
    setData(
      (prevData) =>
        new SubscriptionModel({
          ...prevData,
          selectedContact: contact,
          selectedPaymentMethod: defaultPaymentMethod,
          isNewPaymentMethodPrimary: !defaultPaymentMethod ? true : false,
        })
    );
  }

  function renderSetupFeeInEditMode() {
    if (!data.setupFee) return "-";

    return (
      <CustomLabel>
        ${data.setupFee} <span className="charcoal-label"> USD</span>
      </CustomLabel>
    );
  }

  const renderSubscriptionContent = () => {
    return (
      <InvoicePopupWrapper className="subscription-popup">
        {!AuthService.isMindMeContactRoute && (
          <>
            {data.id && data.selectedContact?.id ? (
              <FlexContainer spacing={30}>
                <CustomLabel lineHeight="22px !important">Customer</CustomLabel>
                <div>
                  <RenderContactInfo contact={data.selectedContact} />
                </div>
              </FlexContainer>
            ) : (
              row(
                "Customer",
                <ContactDropDown
                  contacts={data.contacts || []}
                  selectedContact={data.selectedContact}
                  hasError={data.hasError("selectedContact")}
                  onSelect={(selectedContact) => {
                    onContactChange(selectedContact);
                  }}
                  hasMoreContacts={hasMoreContacts}
                  onRefresh={(
                    contactId,
                    mindmeContactId,
                    searchValue,
                    pageSize
                  ) => {
                    loadContacts(
                      contactId,
                      mindmeContactId,
                      searchValue,
                      pageSize
                    );
                  }}
                  className="white-bg"
                />
              )
            )}
          </>
        )}
        {isEdit &&
          row(
            "Subscription No.",
            <FlexContainer>
              <CustomLabel>{data.subscriptionNumber}</CustomLabel>
            </FlexContainer>,
            20,
            "standard-lh"
          )}
        {textInputRow(
          "Description",
          "description",
          "textarea",
          data.description,
          data.hasError("description"),
          "Enter description"
        )}
        {isEdit &&
          row(
            "Status",

            <FlexContainer alignItems="center" spacing={15}>
              <CustomButton
                color={SubscriptionUtil.getColorsByStatus(data.status).color}
                bgColor={
                  SubscriptionUtil.getColorsByStatus(data.status).bgColor
                }
                className="status-btn"
              >
                {data.status}
              </CustomButton>
              {data.pastDueDays > 0 && (
                <CustomLabel>{data.pastDueDays.toString()} Days</CustomLabel>
              )}
            </FlexContainer>
          )}
        {!isEdit
          ? textInputRow(
              "Setup Fee",
              "setupFee",
              "number",
              data.setupFee,
              data.hasError("setupFee")
            )
          : row("Setup Fee", renderSetupFeeInEditMode())}
        {textInputRow(
          "Recurring Price",
          "recurringPrice",
          "number",
          data.recurringPrice,
          data.hasError("recurringPrice")
        )}
        {textInputRow(
          "Recurring Cost",
          "recurringCost",
          "number",
          data.recurringCost,
          false
        )}
        {!data.hideDueDate &&
          row(
            isEdit ? "Next Payment Due" : "Payment Due",
            <div>
              <FlexContainer fxLayout="column" spacing={5}>
                <div>
                  <FlexContainer alignItems="center" spacing={15}>
                    {textInput(
                      "paymentDueDate",
                      "date",
                      UtilityMethods.toLocalISOString(data.paymentDueDate),
                      "invoice-payment-datepicker",
                      data.hasError("paymentDueDate"),
                      ""
                    )}
                    {/* <TimeComponent
                                            hhmm={data.paymentTime}
                                            error={data.hasError('paymentTime')}
                                            amPm={data.paymentMeredium}
                                            onChange={(amPm: keyof typeof TimeAMPM, time: string) =>
                                                updateTime(amPm, time)
                                            }
                                            className="invoice-timezone subscription-timezone"
                                        /> */}
                    <TextInputWithDropdown
                      textInputValue={data.paymentTime}
                      dropdownValue={data.paymentMeredium}
                      onTextChange={(value, isBlur) =>
                        updateTime(data.paymentMeredium, value, isBlur)
                      }
                      onDropdownChange={(option) =>
                        updateTime(option.value, data.paymentTime, false)
                      }
                      className="invoice-timezone subscription-timezone"
                      dropdownOptions={[
                        Object.keys(TimeAMPM).map((key) => ({
                          value: key,
                          label: TimeAMPM[key as keyof typeof TimeAMPM],
                        })),
                      ]}
                      error={data.hasError("paymentTime")}
                      placeholderForTextInput="hh:mm"
                      isTime={true}
                    />
                    <FlexContainer alignItems="center">
                      <CustomLabel>
                        {AccountSettingsStore.generalSettings.timeZone
                          .abbreviation || ""}
                      </CustomLabel>
                    </FlexContainer>
                    {data.lastPayment ? (
                      <FlexContainer fxLayout="column">
                        <CustomLabel fontSize={"12px"} lineHeight="16px">
                          $
                          {AccountSettingsStore.formatAmount(
                            data.lastPayment.amount
                          )}{" "}
                          paid on
                        </CustomLabel>
                        <CustomLabel fontSize={"12px"} lineHeight="16px">
                          {UtilityMethods.formatDateInString(
                            data.lastPayment.createdDate
                          )}
                        </CustomLabel>
                      </FlexContainer>
                    ) : (
                      ""
                    )}
                  </FlexContainer>
                </div>
                {data.id && (
                  <CustomLabel
                    color="#FF5900"
                    fontSize="12px"
                    lineHeight="16px"
                  >
                    Changing Date or Time for Next Payment Due will reset all
                    scheduled Invoices.
                  </CustomLabel>
                )}
              </FlexContainer>
            </div>,
            20,
            "invoice-payment-due"
          )}
        {row(
          "Bill Every",
          <DescriptionCell>
            <FlexContainer spacing={15} fxLayout="row" className="price-field">
              {textInput(
                "billingIntervalCount",
                "number",
                data.billingIntervalCount === 0
                  ? ""
                  : data.billingIntervalCount.toString(),
                "number-selector bill-every-input",
                data.hasError("billingIntervalCount"),
                ""
              )}
              {singleSelect(
                "billingInterval",
                data.billingInterval,
                "number-selector bill-every-dpdn",
                getBillingOptions(
                  "billingInterval",
                  data.billingIntervalCount > 1
                ),
                data.hasError("billingInterval"),
                ""
              )}
            </FlexContainer>
          </DescriptionCell>
        )}
        {row(
          "Billing Ends",
          <DescriptionCell>
            <FlexContainer>{billingEndsContent()}</FlexContainer>
          </DescriptionCell>
        )}
        {!isEdit &&
          row(
            "Billing Method",
            <BillingMethods
              selectedMethod={data.billingMethod}
              onChange={(value: string) =>
                handleInputChange("billingMethod", value)
              }
            />
          )}
        {data.merchantAccounts.length > 1 &&
          row(
            "Merchant Account",
            <MerchantAccountsDropDown
              selectedMerchantAccount={data.selectedMerchantAccount}
              merchantAccounts={data.merchantAccounts}
              showError={data.hasError("selectedMerchantAccount")}
              onChange={(option) =>
                handleInputChange("selectedMerchantAccount", option)
              }
              className="subscription-merchant-account"
            />
          )}
        {/* {isEdit && (
                    <BillingMethods
                        selectedMethod={data.editBillingMethod}
                        onChange={(value: string) => handleInputChange('editBillingMethod', value)}
                        isEdit={true}
                        showLabel={false}
                    />
                )} */}

        {row(
          "Payment Method",
          <PaymentMethodDropdown
            merchantAccount={data.selectedMerchantAccount}
            selectedPaymentMethod={data.selectedPaymentMethod}
            contactId={data.selectedContact?.id || ""}
            isRenderWithoutContact={
              (!selectedContact?.id && AuthService.isMindMeContactRoute) ||
              data.selectedContact?.mindmeContactId
                ? true
                : false
            }
            paymentMethods={data.paymentMethods}
            onChange={(option) =>
              handleInputChange("selectedPaymentMethod", option)
            }
            hasError={data.hasError("selectedPaymentMethod")}
            className="invoice-payment-method"
            contact={data.selectedContact || null}
            hasErrorInNewPaymentMethod={data.hasError(
              "newPaymentGatewayMethodId"
            )}
            onNewPaymentMethod={(
              isNewPaymentMethod: boolean,
              token: string
            ) => {
              onNewPaymentMethod(isNewPaymentMethod, token);
            }}
            isLodingInProcess={showLoader}
            headerClass="subscription-payment-method"
            updateNewMethodAsPrimary={(isNewPaymentMethodPrimary) =>
              handleInputChange(
                "isNewPaymentMethodPrimary",
                isNewPaymentMethodPrimary
              )
            }
            isNewPaymentMethodPrimary={data.isNewPaymentMethodPrimary}
          />
        )}
        {renderAdvancedSection()}
      </InvoicePopupWrapper>
    );
  };

  const onClickSave = () => {
    if (isAPIRequestPending) return;

    setAPIRequestPending(true);
    if (isEdit) {
      const isValidated = data.validate(false);
      if (isValidated) {
        const apiModelData = data.createEditAPIModel();
        SubscriptionService.updateSubscription(apiModelData).subscribe({
          next: (response) => {
            if (response.result) {
              onClose(true);
              ToastService.showSuccess("Subscription updated successfully");
            } else {
              ToastService.showError("Error in updating subscription");
            }
            setAPIRequestPending(false);
          },
          error: () => {
            setAPIRequestPending(false);
            ToastService.showError("Error in updating subscription");
          },
        });
      } else {
        setAPIRequestPending(false);
        setData(new SubscriptionModel(data));
      }
    } else {
      const isValidated = data.validate(false);
      if (isValidated) {
        const mindmeContactId = AuthService.isMindMeContactRoute
          ? AuthService.getMindMeContactId
          : data.selectedContact?.mindmeContactId || "";
        const apiModelData = data.createAPIModel(mindmeContactId);
        SubscriptionService.createSubscription(apiModelData).subscribe({
          next: (response) => {
            if (response.result.success) {
              onClose(true, response.result.contactId);
              ToastService.showSuccess("Subscription created successfully");
            } else {
              ToastService.showError("Error in creating subscription");
            }
            setAPIRequestPending(false);
          },
          error: () => {
            setAPIRequestPending(false);
            ToastService.showError("Error in creating subscription");
          },
        });
      } else {
        setAPIRequestPending(false);
        setData(new SubscriptionModel(data));
      }
    }
  };

  return (
    <>
      <ModalComponent
        title={isEdit ? `Update Subscription` : "New Subscription"}
        titleFontSize="22px"
        bodyPadding="50px 61px"
        showFooter={true}
        minHeight="440px"
        wrapperWidth="900px"
        bodyBackgroundColor="#fefefe"
        footerBackground="#F4F7FB"
        footerHeight="auto"
        onClose={() => onClose(false)}
        footerContent={
          <FlexContainer
            alignItems="center"
            justifyContent="center"
            spacing={15}
          >
            <SapphireBlueButton
              minWidth="120px"
              height="30px"
              onClick={() => onClose(false)}
            >
              Close
            </SapphireBlueButton>
            <SapphireBlueButton
              primary
              minWidth="120px"
              height="30px"
              onClick={() => onClickSave()}
              disabled={isAPIRequestPending}
            >
              {isEdit ? "Update" : "Save"}
            </SapphireBlueButton>
          </FlexContainer>
        }
      >
        {showLoader
          ? new Loader(true, "", "370px").loaderComponent
          : renderSubscriptionContent()}
      </ModalComponent>
      {showContactPopup && (
        <NewContactPopup
          onClose={(refresh: boolean) => onCloseContact(refresh)}
        />
      )}
    </>
  );
};

export default observer(NewSubscriptionPopUp);
